import ODataUtil from "./ODataUtil";
import { deleteItem, patchItem, postItem, ApiRequest } from './ApiUtils';
import Checklist from 'classes/Checklist';
import buildQuery from 'odata-query';

const checklistEndpoint = "/Checklist";

/**
 * Convert checklists API response to table schema
 */
function convertChecklistsToTableSchema({ "value": items, "@odata.count": count }) {
  // Flatten nested fields in items before passing to convertToTableRows
  const flattenedItems = items.map(item => {
    const flattened = { ...item };

    // Flatten Inspection Number and Inspector from nested expand
    if (item.Ihwi && item.Ihwi.Inspection) {
      flattened.InspectionNumber_Inspection = item.Ihwi.Inspection.InspectionNumber;

      if (item.Ihwi.Inspection.Rassignment && item.Ihwi.Inspection.Rassignment.SystemUser) {
        flattened.Name_SystemUser = item.Ihwi.Inspection.Rassignment.SystemUser.Name;
      }
    }

    return flattened;
  });

  const { data } = Checklist.convertToTableRows({ "value": flattenedItems, "@odata.count": count });
  return { data, count };
}

/**
 * Get checklists with OData query support
 */
async function getChecklists(page, per_page, orderByInfo, orderByDirection, search, filters) {
  try {
    const { skip, count, top, expand, orderBy, mappedFilter } = ODataUtil.UtilityBuilderNestedv3(
      per_page, page, Checklist.expand, orderByInfo, orderByDirection, filters
    );
    const filter = {};
    const isDefaults = filters.find(r => r.defaults);

    if (isDefaults) {
      const defaults = isDefaults.defaults;
      if(defaults.projectId) filter["ihwi/workItem/projectId"] = { eq: { type: 'guid', value: defaults.projectId }};
      if(defaults.inspectionNumber) filter["ihwi/inspection/inspectionNumber"] = defaults.inspectionNumber;
    }

    const allFilters = mappedFilter.reduce((a, b) => ({...a, ...b}), filter);
    const params = buildQuery({ count, top, skip, orderBy, expand, filter: allFilters });

    const data = await ApiRequest(checklistEndpoint, params);
    return convertChecklistsToTableSchema(data);
  } catch (error) {
    console.error('Error fetching checklists:', error);
    throw error;
  }
}

/**
 * Add a new checklist
 */
async function addChecklist(data) {
  try {
    const resourcePath = `${checklistEndpoint}/Add`;
    const result = await postItem(resourcePath, '', data);
    return result;
  } catch (error) {
    console.error('Error adding checklist:', error);
    throw error;
  }
}

/**
 * Update an existing checklist
 */
async function updateChecklist(checklistId, data) {
  try {
    const resourcePath = `${checklistEndpoint}/Update`;
    const params = `?checklistId=${checklistId}`;
    const result = await patchItem(resourcePath, params, data);
    return result;
  } catch (error) {
    console.error('Error updating checklist:', error);
    throw error;
  }
}

/**
 * Delete a checklist
 */
async function deleteChecklist(checklistId) {
  try {
    const resourcePath = `${checklistEndpoint}/Delete`;
    const params = `?checklistId=${checklistId}`;
    const result = await deleteItem(resourcePath, params);
    return result;
  } catch (error) {
    console.error('Error deleting checklist:', error);
    throw error;
  }
}

/**
 * Get a specific checklist by ID
 */
async function getChecklistById(checklistId) {
  try {
    const expand = Checklist.expand;
    const { expand: expandQuery } = ODataUtil.UtilityBuilderNestedv3(
      1,
      0,
      expand,
      null,
      null,
      []
    );

    const params = buildQuery({
      expand: expandQuery,
      filter: { ChecklistId: { eq: { type: 'guid', value: checklistId } } }
    });

    const response = await fetch(`${checklistEndpoint}${params}`);
    const result = await response.json();

    if (result.value && result.value.length > 0) {
      return new Checklist(result.value[0]);
    }

    return null;
  } catch (error) {
    console.error('Error fetching checklist by ID:', error);
    throw error;
  }
}

export {
  getChecklists,
  addChecklist,
  updateChecklist,
  deleteChecklist,
  getChecklistById
};
